// Git: a first-party plugin that brings version control to Slate. It declares
// the deny-by-default `git` permission so the capability is consented and
// revocable like every other native grant, and it is what the marketplace and
// Settings list as the Git feature.
//
// Its UI is a host-registered first-party MAIN-AREA view: the immersive Git
// client (registerGitMainView), opened from the activity bar and gated on
// `capabilities.git` AND this plugin being enabled. So `activate` is a
// deliberate no-op; the plugin exists to own the permission + lifecycle
// (install/uninstall/enable/disable, all of which gate the view in App.tsx).

import type { PluginManifest, PluginModule } from '@slate/plugin-api';

export const GIT_PLUGIN_ID = 'slate.git';

export const GIT_README: string = [
  '# Git',
  '',
  'A full, immersive Git client for Slate. Open it from the activity bar to get a',
  'three-pane workspace: a ref navigator on the left, a commit graph in the',
  'center, and a contextual panel on the right.',
  '',
  '## The client',
  '',
  '- **Left navigator** lists Local and Remote branches, Tags, Stashes and',
  '  Submodules in collapsible sections. Single-click a ref to jump to it in the',
  '  graph; double-click (or the right-click menu) to check it out.',
  '- **Commit graph** shows history with author avatars, ref pills and lane',
  '  colors. Click a commit to inspect it; right-click for branch/tag/checkout',
  '  actions and Isolate. Search branches and commits, and page through history.',
  '- **Right panel** stages and commits your changes (GitKraken-style Unstaged /',
  '  Staged / Summary + Description, with Amend), or shows the selected commit’s',
  '  full message and changed files.',
  '',
  'The toolbar adds undo/redo, stash and pop, fetch, pull and push. Switch between',
  'open repositories from the workspace dropdown, or initialize Git in a folder',
  'that is not yet a repository.',
  '',
  '## Permission',
  '',
  'Git runs version-control operations in your workspace, so it requests the',
  'Version control permission. Disable the plugin any time to remove the Git view',
  'from the activity bar; re-enable it to bring it back.',
  '',
  'Available on the desktop app only.',
].join('\n');

export const GIT_MANIFEST: PluginManifest = {
  id: GIT_PLUGIN_ID,
  name: 'Git',
  version: '1.0.0',
  publisher: 'Slate',
  description:
    'A full, immersive Git client for Slate: branch/tag navigator, commit graph, staging and commit, stashes, and fetch/pull/push.',
  engines: { slate: '^1.0.0' },
  readme: GIT_README,
  permissions: [
    {
      capability: 'git',
      reason: 'Run version-control operations in your workspace',
      scope: 'workspace',
    },
  ],
};

/**
 * The Git plugin has no contributed surfaces to register: its UI is the
 * host-registered immersive Git client (a main-area view), so activation is
 * intentionally empty.
 */
export const gitModule: PluginModule = {
  activate() {
    // No-op: the Git main view is registered by the host (App.tsx) when
    // `capabilities.git` is true and this plugin is enabled.
  },
};
